import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiAward, FiClock, FiHeart } = FiIcons;

function About() {
  const values = [
    {
      icon: FiUsers,
      title: 'Expert Team',
      description: 'Our skilled barbers have years of experience and stay updated with the latest trends.'
    },
    {
      icon: FiAward,
      title: 'Quality Service',
      description: 'We maintain the highest standards of service and use only premium products.'
    },
    {
      icon: FiClock,
      title: 'Timely Service',
      description: 'We respect your time and ensure efficient service without compromising quality.'
    },
    {
      icon: FiHeart,
      title: 'Customer Care',
      description: 'Your satisfaction is our priority. We listen and deliver exactly what you want.'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <section className="bg-red-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            About Us
          </motion.h1>
          <motion.p
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-lg md:text-xl max-w-3xl mx-auto"
          >
            Discover the story behind Ossington Barber Shop and our commitment to providing exceptional grooming services in Toronto.
          </motion.p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Located in the heart of Toronto on Ossington Avenue, our barber shop has been serving the community with premium grooming services. We combine traditional barbering techniques with modern styles to create the perfect look for every client.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our team of skilled barbers brings years of experience and a passion for their craft. We believe that a great haircut is more than just a service – it's an experience that boosts confidence and makes you feel your best.
              </p>
              <p className="text-lg text-gray-600">
                From classic cuts to contemporary styles, beard trims to hot towel shaves, we offer a complete range of services in a welcoming and professional environment.
              </p>
            </motion.div>
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://i.ibb.co/pjkTZ8JM/Ossington-Barber-Shop-image-1.jpg" 
                alt="Ossington Barber Shop Interior" 
                className="rounded-lg shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What We Stand For
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our values guide everything we do, from the moment you walk in until you leave looking and feeling your absolute best.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Experience the Difference
            </h2>
            <p className="text-lg mb-8 max-w-2xl mx-auto">
              Visit us today and discover why Ossington Barber Shop is Toronto's premier destination for professional grooming services.
            </p>
            <a 
              href="tel:+14165327560" 
              className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-200 inline-block text-lg"
            >
              Book Your Visit
            </a>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}

export default About;